import { getFHWAClassDisplayName, getTurnTypeDisplayName, LaneLine, TargetSource, TargetType, TrackedTarget, TrackedTargetHistory } from "@app/shared";
import { LaneLabel } from "@app/shared/src/components/LaneLabel";
import cn from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { getTrackedTargetHistory } from "../TargetHelpers";

type TargetResultProps = {
    target: TrackedTarget;
    currentFrame: { radar: number, video: number };
    onClick?: (target: TrackedTarget) => void;
    height?: number;
    laneLines?: LaneLine[];
};
const precision = 3;

export const TargetResult: React.FC<TargetResultProps> = ({ target, currentFrame, onClick, height = 170, laneLines }: TargetResultProps) => {
    const [isInFrame, setIsInFrame] = useState(false);
    const [currentTrackHistory, setCurrentTrackHistory] = useState<TrackedTargetHistory>();
    useEffect(() => {

        //Is this frame in the range of the target?
        if ((target.source === TargetSource.Camera && (target.lastFrameTracked < currentFrame.video || target.firstFrameTracked > currentFrame.video)) ||
            (target.source === TargetSource.Radar && (target.lastFrameTracked < currentFrame.radar || target.firstFrameTracked > currentFrame.radar))) {
            if (isInFrame) {
                setIsInFrame(false);
            }
            return;
        }
        //We are in the range of the target frames
        if (!isInFrame) {
            setIsInFrame(true);
        }
    }, [target, currentFrame, isInFrame, setIsInFrame]);

    useEffect(() => {
        //Get the current frame details
        if (!isInFrame && currentTrackHistory) {
            setCurrentTrackHistory(undefined);
        }

        if (isInFrame) {
            var newTrackHistory = getTrackedTargetHistory(target, currentFrame);
            setCurrentTrackHistory(newTrackHistory)
        }
    }, [isInFrame, currentFrame, target, setCurrentTrackHistory, currentTrackHistory]);

    const handleOnClick = useCallback(() => {
        onClick?.(target);
    }, [onClick, target]);

    return (
        <div onClick={handleOnClick}
            key={target.radarId}
            className={cn("border-2 p-0 my-1 rounded text-sm",
                (isInFrame ? "border-bright-blue" : "border-separators-page"),
                { "cursor-pointer hover:border-light-blue": (onClick && !currentTrackHistory) })}
            style={{ height: height }} >

            <div className="font-bold bg-background-page-highlight-gray p-2 flex flex-row">
                <div className="flex-grow">
                    Target {target.radarId}: ({TargetType[target.reportedType]}{target.fhwaType ? " FHWA " + getFHWAClassDisplayName(target.fhwaType) : ""})
                    <div className="text-xs font-regular font-semibold font-sm">{target.firstFrameTracked} -&gt; {target.lastFrameTracked} ({target?.source === TargetSource.Radar ? target?.associatedCameraTarget ? "Fused" : "Radar" : "Video"})</div>
                </div>
            </div>

            <div className="px-2 py-1">
                <div className="flex flex-row gap-3">
                    <div className="flex-grow">
                        <div className="font-semibold">Target Details:</div>
                        <div>Source: {TargetSource[target.source]}</div>
                        <div>Lane: {target.laneNumber ? <LaneLabel lanes={target.laneNumber} laneLines={laneLines} emptyValue="None" /> : "None"}</div>
                        <div>Turn: {target.turn ? getTurnTypeDisplayName(target.turn) : "None"}</div>
                        <div>Events: {(target.events?.length ?? 0) > 0 ? target.events!.length + " Events" : "0"}</div>
                    </div>
                    <div className="flex-grow">
                        {isInFrame && <>
                            <div className="font-semibold">{target.source === TargetSource.Radar ? "Radar" : "Video"} Frame {target.source === TargetSource.Radar ? currentFrame.radar : currentFrame.video} ({currentTrackHistory?.pointCount ?? 0} points)</div>
                            <div>Position: ({currentTrackHistory?.position.x.toFixed(precision) ?? 0}, {currentTrackHistory?.position.y.toFixed(precision) ?? 0}, {currentTrackHistory?.position.z.toFixed(precision) ?? 0})</div>
                            <div>Velocity: ({currentTrackHistory?.velocity.x.toFixed(precision) ?? 0}, {currentTrackHistory?.velocity.y.toFixed(precision) ?? 0}, {currentTrackHistory?.velocity.z.toFixed(precision) ?? 0})</div>
                            <div>Acceleration: ({currentTrackHistory?.acceleration.x.toFixed(precision) ?? 0}, {currentTrackHistory?.acceleration.y.toFixed(precision) ?? 0}, {currentTrackHistory?.acceleration.z.toFixed(precision) ?? 0})</div>
                        </>}

                    </div>
                </div>
            </div>
        </div >
    );
};